import React, { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import ForbiddenTagsEditor from "./ForbiddenTagsEditor";
import {
  normalizeExcludedTag,
  normalizeBanWords,
  separateBanWords,
} from "./promptExclusions";
import { sameBanWords } from "./promptDiff";

import { Layers, FileText, Loader2, Send, Undo2, WandSparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { fetcher, getFetcher, postFetcher, useAuthStore } from "api/swr";
import {
  createPromptDraft,
  finishManualEdit,
  reviseDraft,
  selectablePromptModules,
  moduleEditOption,
  PromptDraft,
  readPromptDraft,
  lastUndoableChange,
  undoDraftChange,
} from "./tagPromptDraft";

import { Hint } from "./Hint";
import PromptTranslationDialog from "./PromptTranslationDialog";
import PromptVersionHistory, {
  PromptVersion,
  PromptLogs,
} from "./PromptVersionHistory";
type PromptListResponse = {
  items?: PromptVersion[];
  totalPages?: number;
  latest: PromptVersion | null;
  defaultPrompt: string;
  draftPrompt: string;
};

export default function TagPromptLabPromptPanel({
  prompt,
  onPromptChange,
  onBanWordsChange,
  onFeedbackChange,
  hasProvider,
  onBusyChange,
  onOperationChange,
  generating,
  preview,
  canGenerate = false,
  onTryGenerate,
  onPrepareGeneration,
  preparing = false,
  suggestedChange,
  previewOutcome,
}: {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onBanWordsChange?: (words: string[]) => void;
  onFeedbackChange?: (feedback: string) => void;
  hasProvider: boolean;
  onBusyChange: (busy: boolean) => void;
  onOperationChange?: (working: boolean) => void;
  generating: boolean;
  preview?: React.ReactNode;
  canGenerate?: boolean;
  onTryGenerate?: (draftPrompt: string, banWords: string[]) => Promise<boolean>;
  onPrepareGeneration?: () => void;
  preparing?: boolean;
  suggestedChange?: { id: number; tag: string };
  previewOutcome?: {
    sequence: number;
    prompt: string;
    success: boolean;
    ban_words?: string[];
  };
}) {
  const { toast } = useToast();
  const accountId = useAuthStore(
    (state: any) => state.auth?.user?.id || state.auth?.face?.id || "local",
  );
  const storageKey = `tag-prompt-lab:draft:v1:${accountId}`;
  const [local, setLocal] = useState<PromptDraft | null>(null);
  const [ready, setReady] = useState(false);
  const [storageError, setStorageError] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [message, setMessage] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [unchangedNotice, setUnchangedNotice] = useState("");
  const [previewNotice, setPreviewNotice] = useState("");
  const [phase, setPhase] = useState("");
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const suggestedId = useRef<number | undefined>(undefined);
  const [operation, setOperation] = useState<
    "optimize" | "revise" | "publish" | "discard" | "manual" | "undo" | null
  >(null);
  const [page, setPage] = useState(1);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorText, setEditorText] = useState("");
  const initializedKey = useRef("");
  const localRef = useRef<PromptDraft | null>(null);
  const operationRef = useRef(false);
  const { data, error, mutate } = useSWR<PromptListResponse>(
    ["/tag-prompt-lab/prompts", { page, pageSize: 20 }],
    fetcher,
  );
  const {
    data: globalBans,
    error: bansError,
    mutate: mutateBans,
  } = useSWR<{ ban_words: string[] }>(
    ["/tag-prompt-lab/ban-words", {}],
    fetcher,
  );
  const globalWords = globalBans?.ban_words;
  const [savingBans, setSavingBans] = useState(false);
  const savingBansRef = useRef(false);
  useEffect(() => {
    if (globalWords) onBanWordsChange?.(globalWords);
  }, [globalWords, onBanWordsChange]);
  const latest = data?.latest;
  const modules = useMemo(() => selectablePromptModules(prompt), [prompt]);
  const editOption = moduleEditOption(selectedModule);
  const working = Boolean(operation) || savingBans;
  const busy = working || !globalWords || Boolean(bansError);
  const locked = busy || generating || preparing;
  const undoTarget = useMemo(
    () => (local ? lastUndoableChange(local) : undefined),
    [local],
  );
  const tryAfterEdit = Boolean(canGenerate && onTryGenerate);
  const validPrompt = Boolean(prompt.trim()) && prompt.trim().length <= 16000;

  const save = (next: PromptDraft) => {
    localRef.current = next;
    setLocal(next);
    onPromptChange(next.prompt);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
      setStorageError("");
    } catch {
      setStorageError("草稿未能保存到浏览器，请暂勿关闭页面，稍后重试。");
    }
  };

  useEffect(() => {
    if (initializedKey.current === storageKey) return;
    setReady(false);
    localRef.current = null;
    setLocal(null);
    onPromptChange("");
    try {
      const saved = readPromptDraft(localStorage, storageKey);
      if (saved) {
        initializedKey.current = storageKey;
        save(saved);
        setReady(true);
        return;
      }
    } catch (error) {
      initializedKey.current = storageKey;
      setStorageError(
        `无法读取或迁移本地草稿，原数据已保留：${
          error instanceof Error ? error.message : String(error)
        }。请先备份。`,
      );
      return;
    }
    if (!data) return;
    initializedKey.current = storageKey;
    save(
      createPromptDraft(
        data.draftPrompt || data.latest?.prompt || data.defaultPrompt,
        data.latest?.version ?? null,
        data.latest?.prompt || data.defaultPrompt,
        data.latest?.ban_words,
      ),
    );
    setReady(true);
    // A saved draft is restored once; server revalidation must never replace it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, storageKey, onPromptChange]);

  useEffect(() => {
    if (selectedModule && !modules.includes(selectedModule)) {
      setSelectedModule("");
      toast({ title: "可调整的部分已变化，已切回「按要求调整」" });
    }
  }, [modules, selectedModule, toast]);

  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);

  useEffect(() => {
    onOperationChange?.(working);
    return () => onOperationChange?.(false);
  }, [working, onOperationChange]);

  const updateExcludedTag = async (value: string, action: "add" | "remove") => {
    if (!globalWords || locked || savingBansRef.current) return false;
    savingBansRef.current = true;
    setSavingBans(true);
    try {
      const tag = normalizeExcludedTag(value);
      const result = (await postFetcher("/tag-prompt-lab/ban-words", {
        arg: { tag, action },
      })) as { ban_words: string[] };
      await mutateBans(result, { revalidate: false });
      onBanWordsChange?.(result.ban_words);
      setPreviewNotice("全局禁止列表已保存，当前标签已按列表过滤。");
      toast({
        title: action === "add" ? `禁止标签 ${tag}` : `取消禁止 ${tag}`,
        description: "已全局保存，对所有运营生效，无需发布草稿。",
      });
      return true;
    } catch (error) {
      toast({
        title: "禁止标签未更新",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
      return false;
    } finally {
      savingBansRef.current = false;
      setSavingBans(false);
    }
  };

  useEffect(() => {
    if (
      !suggestedChange ||
      suggestedId.current === suggestedChange.id ||
      !ready ||
      locked
    )
      return;
    suggestedId.current = suggestedChange.id;
    updateExcludedTag(suggestedChange.tag, "add");
    // Consume each click once, after the draft is ready and editable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedChange, ready, locked]);

  useEffect(() => {
    if (generating) {
      setPreviewNotice("正在按当前规则生成标签…");
    } else if (
      previewOutcome?.prompt === prompt &&
      sameBanWords(previewOutcome.ban_words ?? [], globalWords ?? [])
    ) {
      setPreviewNotice(
        previewOutcome.success
          ? "新标签已生成，可在左侧查看变化。"
          : "规则已保留，标签尚未生成成功。可点击“生成标签”重试。",
      );
    } else if (preparing) {
      setPreviewNotice("");
    }
  }, [previewOutcome, prompt, generating, preparing, globalWords]);

  useEffect(() => {
    onFeedbackChange?.([feedback, previewNotice].filter(Boolean).join("\n"));
  }, [feedback, previewNotice, onFeedbackChange]);

  const tryPreview = async (nextPrompt: string) => {
    if (!tryAfterEdit || !onTryGenerate) {
      setPreviewNotice("规则已保留。获取模板后，点击“生成标签”查看效果。");
      return;
    }
    setPhase("正在生成标签…");
    try {
      const success = await onTryGenerate(nextPrompt, globalWords ?? []);
      setPreviewNotice(
        success
          ? "新标签已生成，可在左侧查看变化。"
          : "规则已保留，标签尚未生成成功。可点击“生成标签”重试。",
      );
    } catch {
      setPreviewNotice("规则已保留，标签尚未生成成功。可点击“生成标签”重试。");
    }
  };

  const run = async (
    kind: "optimize" | "revise" | "publish" | "discard" | "manual" | "undo",
    action: () => Promise<void>,
  ) => {
    if (operationRef.current || generating || preparing) return;
    if (tryAfterEdit && ["revise", "manual", "undo"].includes(kind))
      onPrepareGeneration?.();
    operationRef.current = true;
    setOperation(kind);
    setPhase("");
    try {
      await action();
    } catch (err) {
      if (kind === "revise")
        setFeedback(err instanceof Error ? err.message : String(err));
      toast({
        variant: "destructive",
        title:
          kind === "optimize"
            ? "润色失败，原要求已保留"
            : kind === "undo"
            ? "撤销失败，草稿已保留"
            : kind === "manual"
              ? "保存失败，编辑内容已保留"
              : kind === "revise"
                ? "修改失败，草稿已保留"
                : kind === "publish"
                  ? "发布失败，草稿已保留"
                  : "获取最新版本失败，未丢弃草稿",
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      operationRef.current = false;
      setOperation(null);
      setPhase("");
    }
  };

  const saveManual = () =>
    run("manual", async () => {
      const current = localRef.current
        ? finishManualEdit(localRef.current)
        : null;
      if (!current || !editorText.trim() || editorText.length > 16000) return;
      if (current.prompt === editorText) {
        setEditorOpen(false);
        return;
      }
      const after = editorText;
      if (separateBanWords(after).ban_words.length)
        throw new Error(
          "具体禁止标签请在「全局禁止标签 → 管理」中添加，无需修改或发布草稿。",
        );
      const response = (await postFetcher("/tag-prompt-lab/describe-change", {
        arg: { before: current.prompt, after },
      })) as { description: string };
      if (
        typeof response?.description !== "string" ||
        !response.description.trim() ||
        response.description.length > 2000
      )
        throw new Error("修改摘要无效，请重试。");
      if (localRef.current?.prompt !== current.prompt)
        throw new Error("草稿已发生变化，请关闭后重新打开编辑。");
      save(
        reviseDraft(current, after, response.description.trim(), {
          source: "manual",
        }),
      );
      setEditorOpen(false);
      toast({ title: "草稿已保存", description: response.description });
    });

  const optimize = () =>
    run("optimize", async () => {
      const current = localRef.current;
      const request = message.trim();
      if (!current || !request || !hasProvider) return;
      const optimized = (await postFetcher("/tag-prompt-lab/optimize-request", {
        arg: {
          prompt: current.prompt,
          message: request,
          module: (advancedOpen && selectedModule) || undefined,
        },
      })) as { message: string };
      if (
        typeof optimized?.message !== "string" ||
        !optimized.message.trim() ||
        optimized.message.length > 2000
      )
        throw new Error("优化结果无效，请重试。");
      if (localRef.current?.prompt !== current.prompt)
        throw new Error("规则已发生变化，请重新润色。");
      const wording = optimized.message.trim();
      setMessage(wording);
      toast({
        title: wording === request ? "当前要求已清晰，无需润色" : "已优化并补充修改要求",
        description: "可继续编辑，确认后点击修改规则并试生成。",
      });
    });

  const revise = (
    request = message.trim(),
    scope = advancedOpen ? selectedModule : "",
  ) =>
    run("revise", async () => {
      const current = localRef.current
        ? finishManualEdit(localRef.current)
        : null;
      if (!current || !request) return;
      setFeedback("");
      setPreviewNotice("");
      setPhase("正在理解调整要求…");
      save(current);
      setPhase("正在修改规则…");
      const response = (await postFetcher("/tag-prompt-lab/revise", {
        arg: {
          prompt: current.prompt,
          message: request,
          module: scope || undefined,
        },
      })) as { prompt: string; reply: string };
      if (!response?.prompt?.trim() || response.prompt.length > 16000)
        throw new Error("返回的提示词为空或超过 16000 字，请重试。");
      if (separateBanWords(response.prompt).ban_words.length)
        throw new Error(
          "具体禁止标签请在「全局禁止标签 → 管理」中添加，无需修改或发布草稿。",
        );
      const next = reviseDraft(
        current,
        response.prompt,
        response.reply || request,
        {
          request,
          module: scope || null,
        },
      );
      const changed = next !== current;
      if (changed) {
        save(next);
        setMessage("");
        const receipt = `已新增第 ${next.logs.length} 条修改记录。`;
        setFeedback(`${receipt}\n${response.reply || "规则已更新。"}`);
        toast({ title: "草稿已更新", description: receipt });
      } else {
        // A model's summary is not evidence of a change. Keep the request so the
        // operator can clarify it, and do not invent an empty patch/history item.
        const notice =
          "AI 返回的提示词与原文相同，未新增修改记录。" +
          (tryAfterEdit ? "本次仍用原规则试生成；" : "") +
          "你的修改要求已保留，可以补充后重试。";
        setFeedback(
          response.reply ? `${notice}\nAI 说明：${response.reply}` : notice,
        );
        setUnchangedNotice(notice);
      }
      await tryPreview(next.prompt);
    });

  const undo = () =>
    run("undo", async () => {
      if (!localRef.current) return;
      const next = undoDraftChange(localRef.current);
      if (next === localRef.current) return;
      save(next);
      setFeedback(next.logs[next.logs.length - 1].description);
      setPreviewNotice("");
      await tryPreview(next.prompt);
    });

  const publish = () =>
    run("publish", async () => {
      const current = localRef.current
        ? finishManualEdit(localRef.current)
        : null;
      if (!current) return;
      save(current);
      const version = (await postFetcher("/tag-prompt-lab/prompts", {
        arg: {
          prompt: current.prompt,
          ban_words: current.ban_words,
          base_ban_words: current.base_ban_words,
          note:
            current.logs.length === 1
              ? current.logs[0].description.split(/\r?\n/)[0].slice(0, 200)
              : current.logs.length > 1
                ? `优化打标提示词（共 ${current.logs.length} 次修改）`
                : "发布本地草稿",
          logs: current.logs,
          historyFormat: 2,
          baseVersion: current.baseVersion,
          basePrompt: current.basePrompt,
          updatedAt: current.updatedAt,
        },
      })) as PromptVersion;
      if (
        !version?.id ||
        version.prompt !== current.prompt ||
        !sameBanWords(
          normalizeBanWords(version.ban_words ?? []),
          current.ban_words,
        ) ||
        version.historyFormat !== 2 ||
        version.logCount !== current.logs.length ||
        !Number.isInteger(version.version)
      )
        throw new Error("发布响应无效，请刷新最新版本确认。");
      // Keep editing this same draft after publishing; only its base changes.
      save({
        ...current,
        logs: [],
        baseVersion: version.version,
        basePrompt: version.prompt,
        base_ban_words: [...current.ban_words],
        updatedAt: Date.now(),
      });
      await mutate(
        (previous) => ({
          ...previous,
          latest: version,
          totalPages: Math.max(1, Math.ceil(version.version / 20)),
          items: [
            version,
            ...(previous?.items || []).filter((item) => item.id !== version.id),
          ].slice(0, 20),
          defaultPrompt: version.prompt,
          draftPrompt: version.prompt,
        }),
        { revalidate: true },
      );
      setPage(1);
      setPublishOpen(false);
      toast({ title: `已发布 v${version.version}，可继续编辑草稿` });
    });

  const discard = () =>
    run("discard", async () => {
      // Fetch first: a failed refresh must not destroy the current draft.
      const fresh = (await getFetcher("/tag-prompt-lab/prompts", {
        page: 1,
        pageSize: 20,
      })) as PromptListResponse;
      const next =
        fresh.draftPrompt || fresh.latest?.prompt || fresh.defaultPrompt;
      if (!next?.trim()) throw new Error("最新提示词为空");
      save(
        createPromptDraft(
          next,
          fresh.latest?.version ?? null,
          fresh.latest?.prompt || fresh.defaultPrompt,
          fresh.latest?.ban_words,
        ),
      );
      setPage(1);
      initializedKey.current = storageKey;
      setReady(true);
      setSelectedModule("");
      setMessage("");
      setDiscardOpen(false);
      setFeedback("");
      setPreviewNotice("");
      await mutate(fresh, { revalidate: false });
    });

  return (
    <section
      className="flex shrink-0 flex-col gap-3 xl:min-h-0 xl:flex-1"
      aria-label="编辑提示词"
    >
      {!preview ? (
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <FileText className="h-4 w-4" />
            编辑提示词
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            在右侧描述调整要求，修改会自动保存到本地草稿。
          </p>
        </div>
      ) : null}
      {storageError ? (
        <p role="alert" className="text-sm text-destructive">
          {storageError}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          版本加载失败，本地草稿仍会保留。
          <button className="ml-2 underline" onClick={() => void mutate()}>
            重试
          </button>
        </p>
      ) : null}
      <div
        className={`grid items-stretch gap-3 xl:min-h-0 xl:flex-1 ${
          preview
            ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,0.5fr)_minmax(0,0.5fr)]"
            : "lg:grid-cols-[minmax(340px,0.95fr)_minmax(0,1.4fr)]"
        }`}
      >
        {preview}
        <Card className="flex h-full min-h-0 min-w-0 flex-col shadow-none">
          <CardContent className="flex min-h-0 flex-1 flex-col p-3">
            <PromptVersionHistory
              versions={data?.items || (latest ? [latest] : [])}
              latestVersion={latest?.version}
              page={page}
              totalPages={data?.totalPages || 1}
              onPageChange={setPage}
              loading={!data}
              draftItem={
                <div
                  className="flex h-20 w-full shrink-0 flex-col justify-between rounded-lg border border-primary/40 bg-primary/5 p-3"
                  aria-label="本地草稿版本"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Hint content="手动编辑提示词正文">
                      <button
                        type="button"
                        className="shrink-0 text-sm font-semibold text-blue-600 underline underline-offset-2 hover:text-blue-700 disabled:opacity-50 dark:text-blue-400"
                        disabled={!ready || locked}
                        onClick={() => {
                          setEditorText(localRef.current?.prompt || "");
                          setEditorOpen(true);
                        }}
                      >
                        本地草稿
                      </button>
                    </Hint>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        disabled={locked || (!ready && !storageError)}
                        onClick={() => setDiscardOpen(true)}
                      >
                        丢弃草稿
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={
                          !ready ||
                          busy ||
                          generating ||
                          !validPrompt ||
                          !data ||
                          Boolean(error) ||
                          (prompt.trim() === latest?.prompt.trim() &&
                            sameBanWords(
                              local?.ban_words ?? [],
                              latest?.ban_words ?? [],
                            ))
                        }
                        onClick={() => setPublishOpen(true)}
                      >
                        发布草稿
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">
                      {ready
                        ? `基于 ${
                            local?.baseVersion
                              ? `v${local.baseVersion}`
                              : "内置基准"
                          } · ${local?.logs.length || 0} 次修改${
                            latest &&
                            local?.baseVersion &&
                            latest.version > local.baseVersion
                              ? ` · 最新 v${latest.version}`
                              : ""
                          }`
                        : "读取中…"}
                    </span>
                    <button
                      type="button"
                      className="shrink-0 cursor-pointer text-blue-600 underline underline-offset-2 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:text-blue-400 dark:hover:text-blue-300"
                      disabled={!ready}
                      onClick={() => setLogsOpen(true)}
                    >
                      修改记录
                    </button>
                  </div>
                </div>
              }
            />
          </CardContent>
        </Card>
        <Card
          className="flex h-full min-h-0 min-w-0 flex-col shadow-none"
          aria-label="提示词聊天"
        >
          <CardHeader className="px-4 pb-0 pt-4">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">调整标签规则</CardTitle>
            </div>
            <p className="text-xs text-muted-foreground">
              这里调整的是所有模板共用的生成规则。先用当前图片试效果，满意后再发布。
            </p>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            {bansError ? (
              <p role="alert" className="text-xs text-destructive">
                全局禁止词加载失败，暂不可生成标签。
                <button type="button" onClick={() => void mutateBans()}>
                  重试
                </button>
              </p>
            ) : !globalWords ? (
              <p className="text-xs text-muted-foreground">
                正在读取全局禁止词…
              </p>
            ) : null}
            <ForbiddenTagsEditor
              prompt={prompt}
              words={globalWords ?? []}
              disabled={locked}
              onEdit={updateExcludedTag}
            />
            <label htmlFor="tag-rule-request" className="text-sm font-medium">
              这些标签哪里需要调整？
            </label>
            <Textarea
              id="tag-rule-request"
              ref={messageRef}
              aria-label="本轮修改要求"
              value={message}
              maxLength={2000}
              rows={7}
              className="min-h-[140px] flex-1 resize-none"
              disabled={!ready || locked || !hasProvider}
              placeholder={
                advancedOpen && selectedModule
                  ? editOption.example
                  : "例如：多一些有依据的情感表达；不要服装颜色；标签太泛，希望突出动作。"
              }
              onChange={(event) => setMessage(event.target.value)}
            />
            {feedback && !onFeedbackChange ? (
              <div
                role="status"
                aria-label="本次规则调整"
                className="space-y-1 rounded-lg bg-muted/30 px-3 py-2 text-xs"
              >
                <p className="whitespace-pre-line leading-5">{feedback}</p>
                {previewNotice ? (
                  <p className="text-muted-foreground">{previewNotice}</p>
                ) : null}
              </div>
            ) : null}
            <details
              open={advancedOpen}
              onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}
              className="shrink-0 rounded-lg border px-3 py-2"
            >
              <summary className="cursor-pointer text-xs text-muted-foreground">
                高级设置
                {advancedOpen && selectedModule
                  ? ` · ${moduleEditOption(selectedModule).label}`
                  : " · 默认自动选择调整部分"}
              </summary>
              <div className="mt-3 space-y-2">
                <label htmlFor="tag-prompt-lab-module" className="text-xs">
                  只调整指定部分（可选）
                </label>
                <div className="relative">
                  <Layers
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  />
                  <Select
                    value={selectedModule || "auto"}
                    disabled={!ready || locked}
                    onValueChange={(value) =>
                      setSelectedModule(value === "auto" ? "" : String(value))
                    }
                  >
                    <SelectTrigger
                      id="tag-prompt-lab-module"
                      aria-label="选择模块"
                      className="h-10 w-full pl-9"
                    >
                      <SelectValue placeholder="自动选择（推荐）" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">自动选择（推荐）</SelectItem>
                      {modules.map((title) => (
                        <SelectItem key={title} value={title}>
                          {moduleEditOption(title).label} - {title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  {editOption.help}
                </p>
              </div>
            </details>
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
              <Hint
                content={
                  undoTarget
                    ? `撤销：${undoTarget.log.description.split(/\r?\n/)[0]}`
                    : "暂无可撤销的草稿修改"
                }
              >
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!ready || locked || !undoTarget}
                  onClick={() => void undo()}
                >
                  <Undo2 className="mr-1 h-4 w-4" />
                  撤销上次修改
                </Button>
              </Hint>
              <div className="flex items-center gap-2">
                <Hint
                  content={
                    operation === "optimize"
                      ? "正在润色…"
                      : "提示词优化助手：结合当前规则扩写修改要求"
                  }
                >
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  aria-label="润色修改要求"
                  aria-busy={operation === "optimize"}
                  disabled={!ready || locked || !hasProvider || !validPrompt || !message.trim()}
                  onClick={() => void optimize()}
                >
                  {operation === "optimize" ? (
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  ) : (
                    <WandSparkles aria-hidden="true" className="h-4 w-4" />
                  )}
                </Button>
                </Hint>
                <Button
                  className="px-3"
                  disabled={
                    !ready ||
                    locked ||
                    !validPrompt ||
                    !message.trim() ||
                    !hasProvider
                  }
                  onClick={() => void revise()}
                >
                  {operation === "revise" || operation === "undo" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {phase || (tryAfterEdit ? "修改规则并试生成" : "保存规则")}
                </Button>
              </div>
            </div>
            {!canGenerate ? (
              <p className="text-xs text-muted-foreground">
                先获取一张模板图片，就能在修改后直接试生成。
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
      <Dialog
        open={editorOpen}
        onOpenChange={(open) => {
          if (!busy) setEditorOpen(open);
        }}
      >
        <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
          <DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
              <DialogTitle>手动编辑本地草稿</DialogTitle>
              <PromptTranslationDialog text={editorText} disabled={busy} />
            </div>
            <DialogDescription>
              保存时根据实际差异生成简短摘要，并追加修改记录。
            </DialogDescription>
          </DialogHeader>
          <Textarea
            aria-label="草稿正文"
            value={editorText}
            onChange={(event) => setEditorText(event.target.value)}
            maxLength={16000}
            disabled={busy}
            className="min-h-0 flex-1 font-mono text-xs leading-6"
            style={{ height: "50vh", flexBasis: "50vh" }}
          />
          <p className="text-right text-xs text-muted-foreground">
            {editorText.length} / 16000
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setEditorOpen(false)}
            >
              取消编辑
            </Button>
            <Button
              disabled={
                busy ||
                !hasProvider ||
                !editorText.trim() ||
                editorText.length > 16000 ||
                editorText === local?.prompt
              }
              onClick={() => void saveManual()}
            >
              {operation === "manual" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {operation === "manual" ? "生成摘要中…" : "保存并生成记录"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(unchangedNotice)}
        onOpenChange={(open) => {
          if (!open) setUnchangedNotice("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>提示词未修改</DialogTitle>
            <DialogDescription>{unchangedNotice}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setUnchangedNotice("")}>知道了</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>草稿修改记录</DialogTitle>
            <DialogDescription>
              查看每次修改的总结和具体差异。
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 overflow-auto">
            <PromptLogs
              logs={local?.logs || []}
              basePrompt={local?.basePrompt}
              finalPrompt={local?.prompt}
            />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={publishOpen}
        onOpenChange={(open) => {
          if (!busy) setPublishOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认发布草稿？</DialogTitle>
            <DialogDescription>
              当前草稿将发布为新的提示词版本，{local?.logs.length || 0}{" "}
              次修改的记录会一并保存。发布后仍可继续编辑草稿。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setPublishOpen(false)}
            >
              取消发布
            </Button>
            <Button disabled={busy} onClick={() => void publish()}>
              {operation === "publish" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              确认发布
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={discardOpen}
        onOpenChange={(open) => {
          if (!busy) setDiscardOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>丢弃本地草稿？</DialogTitle>
            <DialogDescription>
              当前草稿及未发布的修改记录将被丢弃，并从最新发布版本重新开始。此操作无法撤销。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setDiscardOpen(false)}
            >
              继续编辑
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => void discard()}
            >
              {operation === "discard" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              丢弃并重新开始
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
